import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/components/Brand";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | failed
  const tries = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await api.get(`/billing/status`);
        if (cancelled) return;
        if (res.data?.has_access) {
          setState("paid");
          await checkAuth(); // refresh user so subscription_status is now active
          return;
        }
      } catch (e) {
        // keep trying
      }
      tries.current += 1;
      if (tries.current > 30) {
        setState("failed");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 text-center" data-testid="payment-success">
      <div className="mb-6">
        <BrandMark className="h-14 w-14 object-contain mx-auto" />
      </div>
      {state === "checking" && (
        <div className="flex items-center gap-3 text-muted-foreground" data-testid="payment-checking">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Confirming your payment…</span>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="payment-paid" className="w-full max-w-lg text-left">
          <div className="flex items-center gap-3 justify-center mb-6">
            <CheckCircle2 className="h-8 w-8 text-primary" />
            <p className="font-heading text-2xl text-foreground">You're all set!</p>
          </div>
          <div className="rounded-2xl border border-border bg-card shadow-soft p-6" data-testid="purchase-acknowledgment">
            <p className="text-sm font-semibold text-foreground">Subscription confirmation</p>
            <p className="text-sm text-foreground/80 mt-3 leading-relaxed">
              You were charged <strong>$219 today</strong>, which includes a one-time <strong>$149</strong> setup and
              onboarding fee and your first <strong>$70</strong> monthly subscription payment.
            </p>
            <p className="text-sm text-foreground/80 mt-3 leading-relaxed">
              Your Work &amp; Keep subscription continues at <strong>$70 per month</strong> until canceled. To prevent
              the next recurring charge, cancel before your next billing date.
            </p>
            <p className="text-sm text-foreground/80 mt-3 leading-relaxed">
              <strong>How to cancel:</strong> go to <strong>Settings &rarr; Manage subscription</strong> to open the
              Stripe customer portal, where you can cancel or update your plan online at any time. Cancellation takes
              effect at the end of your current paid billing period, and access continues through that period.
            </p>
            <p className="text-xs text-muted-foreground mt-4">
              See our <Link to="/terms" className="underline hover:text-foreground">Terms of Service</Link> for complete
              refund and cancellation terms. Please keep this confirmation for your records.
            </p>
          </div>
          <button onClick={() => navigate("/setup", { replace: true })} data-testid="continue-to-setup"
            className="w-full rounded-full h-12 mt-6 bg-primary hover:bg-primary/90 text-primary-foreground text-base font-medium transition-colors">
            Continue to setup
          </button>
        </div>
      )}
      {state === "failed" && (
        <div data-testid="payment-failed">
          <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-3" />
          <p className="font-heading text-2xl text-foreground">We couldn't confirm your payment</p>
          <button onClick={() => navigate("/billing", { replace: true })}
            className="text-primary font-medium mt-3 hover:underline" data-testid="payment-back-billing">
            Return to billing
          </button>
        </div>
      )}
    </div>
  );
}
